import Navmenu from "../assets/svgs/iphone/navmenu.svg";
import PlayButton from "../assets/svgs/iphone/play.svg";
import BackButton from "../assets/svgs/iphone/back.svg";
import Share from "../assets/svgs/iphone/share.svg";
import Time from "../assets/svgs/iphone/time.svg";
import { InstagramIcon } from "../assets/svgs/common/instagram.svg";
import FaceIcon from "../assets/svgs/common/face.svg";
import millify from "millify";
import Reward from "../assets/svgs/campaigns/reward.svg";
import InfoIcon from "../assets/svgs/common/info.svg";
import SliderIcon from "../assets/svgs/campaigns/slider-menu.svg";
import { useEffect, useState, type FunctionComponent } from "react";
import type { IzCampaignFormAdmin } from "../z/admin/campaign-form.admin.z";
import type { UseFormReturn } from "react-hook-form";
import AddImageIcon from "../assets/svgs/common/add-image.svg";
import Coin from "../assets/svgs/campaigns/coin.scg";
import LivePreviewIcon from "../assets/svgs/common/live-preview.svg";
import { YoutubeIcon } from "lucide-react";
import YTShort from "../assets/svgs/common/yt-short.svg";
import { SocialPostType } from "../types/models/campaign.models.types";

type AppProps = {
  campaignForm: UseFormReturn<IzCampaignFormAdmin>;
};

const CreateCampaignPreview: FunctionComponent<AppProps> = ({
  campaignForm,
}) => {
  const [form, setForm] = useState<IzCampaignFormAdmin>(
    campaignForm.getValues(),
  );

  useEffect(() => {
    campaignForm.watch(() => {
      setForm(campaignForm.getValues());
    });
  }, [campaignForm]);

  function ReturnPostType(postType: SocialPostType) {
    if (postType === SocialPostType.REEL) {
      return <InstagramIcon />;
    }
    if (postType === SocialPostType.YOUTUBE_SHORT) {
      return <YTShort />;
    }
    if (postType === SocialPostType.YOUTUBE_VIDEO) {
      return <YoutubeIcon width={10} />;
    }
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex justify-between items-start pt-16 w-full">
        <div className="top-40 sticky flex justify-center items-center p-1 border-secondary border-b border-l rounded-3xl w-72 h-[600px] overflow-hidden b-g-black">
          <div className="flex flex-col justify-between bg-background border border-border rounded-[20px] w-full h-full overflow-hidden">
            <div className="relative flex flex-col justify-start items-center gap-3 w-full h-full">
              <div className="top-0 z-20 absolute flex justify-center items-center w-full h-10">
                <div className="flex justify-center items-center bg-black p-1 border-secondary border-r rounded-full">
                  <div className="bg-black border-secondary border-r rounded-full w-1 h-1" />
                </div>
              </div>

              <div className="z-10 absolute flex justify-center items-center bg-gradient-to-b from-black to-transparent w-full h-10 via">
                <Navmenu />
              </div>
              <div className="relative border-border border-b rounded-xl w-full h-44 overflow-hidden">
                <div className="top-0 z-10 absolute flex justify-between items-center px-3 pt-10 w-full">
                  <BackButton />
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Time />
                      <p className="font-semibold text-[9px]">
                        {form.startDate && form.endDate
                          ? `${(
                              (new Date(form.endDate).getTime() -
                                new Date(form.startDate).getTime()) /
                              (1000 * 60 * 60 * 24)
                            ).toFixed(0)} days`
                          : ""}
                      </p>
                    </div>
                    <Share />
                  </div>
                </div>

                {form.coverImageFile?.[0]?.file || form.coverImage?.url ? (
                  <img
                    className="w-full h-full object-cover"
                    src={
                      (form.coverImageFile?.[0]?.file &&
                        URL.createObjectURL(form.coverImageFile[0].file)) ||
                      form.coverImage?.url
                    }
                    alt=""
                  />
                ) : (
                  <div className="top-0 left-0 absolute flex justify-center items-center w-full h-full">
                    <AddImageIcon />
                  </div>
                )}

                <div className="bottom-0 absolute flex items-center gap-2 bg-gradient-to-t from-background to-transparent px-3 py-2 w-full">
                  <PlayButton />
                  <p className="font-semibold text-[9px]">
                    How to apply for this challenge
                  </p>
                </div>
              </div>
              <div className="flex justify-between items-center w-full">
                <div className="flex flex-col gap-2 pl-3 w-3/5 text-wrap">
                  <div className="flex items-center gap-1 h-3">
                    {form.socialPostType && (
                      <span className="flex items-center gap-1 text-nowrap px-2 py-[2px] border border-border rounded-2xl font-semibold text-[10px]">
                        {ReturnPostType(form.socialPostType)}
                        {form.socialPostType.replace(/_/g, " ")}
                      </span>
                    )}
                    {form.socialCreatorType && (
                      <span className="flex items-center gap-1 px-2 py-[2px] border border-border rounded-2xl font-semibold text-[10px]">
                        <FaceIcon />
                        {form.socialCreatorType}
                      </span>
                    )}
                  </div>
                  <p className="w-full font-bold text-sm text-wrap">
                    {form.name || (
                      <span className="text-secondary">Campaign name</span>
                    )}
                  </p>
                </div>
                <div className="text-right flex flex-col bg-primarybuttonbg py-2 pr-3 pl-6 rounded-l-xl text-primarybuttontext">
                  <span className="font-semibold text-[8px] text-secondary">
                    Your max payout
                  </span>
                  <strong className="font-semibold text-md">
                    ₹
                    {millify(
                      form.milestones?.at(form.milestones.length - 1)?.payout ||
                        0,
                    )}
                  </strong>
                </div>
              </div>
              <div className="flex justify-between items-center gap-4 p-1 border border-border rounded-3xl w-11/12 font-semibold text-[10px]">
                <span className="bg-white px-6 py-2 rounded-2xl text-black">
                  About this collab
                </span>
                <span className="px-4 py-2">Leaderboard</span>
              </div>
              <div className="border-border border-t w-full" />
              <div className="flex flex-col items-center gap-5 px-3 w-full font-semibold text-sm">
                <div className="flex justify-between items-center w-full">
                  <span className="">Milestones</span>
                  <span>0/{form.milestones?.length || 0}</span>
                </div>
                <div className="flex justify-around bg-tertiarybuttonbg rounded-3xl w-full h-5 text-[7px]">
                  {form.milestones?.map((milestone, index: number) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                      key={index}
                      className="flex justify-between items-center px-3"
                    >
                      <div className="relative flex flex-col items-center -mt-[3px] font-semibold leading-3">
                        <span className="border-r h-1" />
                        <span>{millify(milestone.count)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-nowrap gap-2 w-96 translate-x-16 overflow-x-hidden">
                  {form.productRefund?.enabled && (
                    <div className="relative flex justify-between rounded-lg h-28 text-[10px] overflow-hidden">
                      <div
                        className={
                          "absolute flex justify-center items-center w-full h-48  text-white"
                        }
                      >
                        ₹{millify(form.productRefund?.refundAmount || 0)}
                      </div>

                      <Coin />
                    </div>
                  )}
                  {form.milestones
                    ?.slice(0, form.productRefund?.enabled ? 3 : 4)
                    .map((milestone, index: number) => {
                      return (
                        <div
                          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                          key={index}
                          className="relative flex justify-between border border-border rounded-lg h-28 text-[10px] overflow-hidden"
                        >
                          <div className="absolute flex justify-center items-center w-full h-7 text-black">
                            {millify(milestone.count)}&nbsp;
                            <span className="text-secondary">views</span>
                          </div>
                          <div className="absolute flex justify-center items-center w-full h-48 text-yellow-500">
                            ₹{millify(milestone.payout)}
                          </div>
                          <Reward />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 pb-2 w-full">
              <div className="flex justify-between items-center px-3 w-full">
                <div className="flex items-center gap-2 font-semibold">
                  <InfoIcon />
                  <div className="flex flex-col">
                    <p className="text-[10px]">
                      Views will get updated every 4 hours
                    </p>
                    <span className="text-[7px] text-secondary">
                      Last updated 2 hours ago
                    </span>
                  </div>
                </div>
                <SliderIcon />
              </div>
              <div className="opacity-50 border-border border-t w-full" />
            </div>
          </div>
        </div>
      </div>
      <p className="flex justify-center items-center gap-2 font-semibold text-base">
        <LivePreviewIcon />
        Live preview
      </p>
    </div>
  );
};

export default CreateCampaignPreview;
